<template>
  <div class="message-body row">
    <div class="col-md-8 col-12">
      <segment>
        <div>
          <div class="languages-row">
            <div class="language-tabs inline">
              <div
                  v-for="item in Object.keys(modelProp)"
                  v-if="isLanguage(item)"
                  v-bind:key="item" class="language-tab"
                  :class="activeLang === item ? 'active' : ''"
                  @click="() => toggleActiveLanguage(item)"
              >
                {{ languages.find(elem => elem.code === item) ? languages.find(elem => elem.code === item).name : '' }}
              </div>
            </div>
            <a class="add-language-button" @click="() => toggleLanguageModal(true)">
              <i class="fas fa-pen" />
              <p>ADD LANGUAGES</p>
            </a>
            <a class="translate-ai-button"
               :class="{ disabled: !canTranslate }"
               @click="translateWithAI">
              <i class="fas fa-language" />
              <p>TRANSLATE WITH AI</p>
            </a>
            <a class="translate-ai-button"
               :class="{ disabled: !canShorten }"
               :title="activeLang === 'default' ? 'Pick a translated language tab to shorten its content' : ''"
               @click="shortenWithAI">
              <i class="fas fa-compress-alt" />
              <p>SHORTEN WITH AI</p>
            </a>
            <div id="language-modal"
                 v-if="languageModalOpen"
                 @click.self="toggleLanguageModal(false)">
              <div class="language-modal-container">
                <div class="header">
                  <span>Add Languages</span>
                  <span class="close-btn" @click="toggleLanguageModal(false)">&times;</span>
                </div>
                <div class="search-row">
                  <i class="fas fa-search search-icon" />
                  <input ref="languageSearchInput"
                         v-model="languageSearch"
                         type="text"
                         class="language-search-input"
                         placeholder="Search language..." />
                  <i v-if="languageSearch"
                     class="fas fa-times clear-icon"
                     @click="languageSearch = ''" />
                </div>
                <div class="language-list">
                  <div v-if="filteredSelectedLanguages.length === 0 && filteredAvailableLanguages.length === 0"
                       class="language-empty">
                    No languages match "{{ languageSearch }}"
                  </div>

                  <template v-if="filteredSelectedLanguages.length > 0">
                    <div class="language-section-header">
                      Selected ({{ selectedLanguageCount }})
                    </div>
                    <div
                        v-for="language in filteredSelectedLanguages"
                        :key="'sel-' + language.code"
                        class="language-row selected"
                        :class="{ locked: language.code === 'default' }"
                        @click="toggleLanguage(language.code)">
                      <span class="language-row-name">{{ language.name }}</span>
                      <span class="language-row-trailing">
                        <i v-if="language.code === 'default'" class="fas fa-lock" title="English is required" />
                        <i v-else class="fas fa-times remove-icon" title="Remove" />
                      </span>
                    </div>
                  </template>

                  <template v-if="filteredAvailableLanguages.length > 0">
                    <div class="language-section-header">
                      Available
                    </div>
                    <div
                        v-for="language in filteredAvailableLanguages"
                        :key="'avl-' + language.code"
                        class="language-row"
                        @click="toggleLanguage(language.code)">
                      <span class="language-row-name">{{ language.name }}</span>
                      <span class="language-row-trailing">
                        <i class="fas fa-plus add-icon" title="Add" />
                      </span>
                    </div>
                  </template>
                </div>
                <div class="modal-footer">
                  <button class="modal-done-btn" @click="toggleLanguageModal(false)">Done</button>
                </div>
              </div>
            </div>
          </div>
          <input-group placeholder="Title" label="Title" v-model="title" />
          <input-group placeholder="Subtitle" label="Subtitle" v-model="subtitle" />
          <input-group placeholder="Message" label="Message" type="textarea" v-model="body" />
          <input-group placeholder="Image url" label="Upload Image or enter image url (Optional)" type="textarea" v-model="image" />
          <div class="row">
            <input type="file"  @change="getImage('image')" ref="image" >
          </div>
          <div v-if="translationError" class="translation-error">{{ translationError }}</div>
          <div v-if="shortenError" class="translation-error">{{ shortenError }}</div>
<!--          <input-group placeholder="htt://bit.ly.abc" label="Launch URL" v-model="modelProp[activeLang].launchUrl" />-->
        </div>
      </segment>
    </div>
    <div class="col-md-4 col-12 phone-frame-container">
      <div class="phone-frame-wrapper">
        <img src="../../../../assets/images/phone-frame.png" style="width: 80%;" />
        <div class="notification-container">
          <div class="notification-header">
            <div class="notification-app-name">
              App Name
            </div>
            <div class="notification-app-time">
              now
            </div>
          </div>
          <div class="notification-body">
            <div class="title">
              {{ modelProp[activeLang].title }}
            </div>
            <div class="subtitle">
              {{ modelProp[activeLang].subtitle }}
            </div>
            <div class="content">
              {{ modelProp[activeLang].body }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="translate-spinner" class="spinner" v-show="isTranslating || isShortening">
      <Spinner />
    </div>
  </div>
</template>

<script>
import Segment from "@/views/PushNotification/UIComponents/Segment";
import InputGroup from "@/views/PushNotification/UIComponents/InputGroup";
import Spinner from "@/components/Spinner.vue";
import importMedia from "@/util/importMedia";
import { managerService } from "@/services/manager.service";

const TRANSLATABLE_FIELDS = ["title", "subtitle", "body"];

export default {
  name: "MessageBody",
  components: {
    Segment,
    InputGroup,
    Spinner
  },
  model: {
    prop: "modelProp",
    event: "modelEvent"
  },
  props: {
    modelProp: Object
  },
  computed: {
    languages() {
      return this.$store.getters.audienceOptions && this.$store.getters.audienceOptions.find(item => item.key === 'language') ? this.$store.getters.audienceOptions.find(item => item.key === 'language').options.map(item => item.code === 'en' ? {...item, code: 'default'} : item)  : []
    },
    title: {
      get() {
        return this.modelProp[this.activeLang].title;
      },
      set(value) {
        const newModelProp = { ...this.modelProp };
        newModelProp[this.activeLang].title = value;
        this.$emit('modelEvent', newModelProp);
      }
    },
    subtitle: {
      get() {
        return this.modelProp[this.activeLang].subtitle;
      },
      set(value) {
        const newModelProp = { ...this.modelProp };
        newModelProp[this.activeLang].subtitle = value;
        this.$emit('modelEvent', newModelProp);
      }
    },
    body: {
      get() {
        return this.modelProp[this.activeLang].body;
      },
      set(value) {
        const newModelProp = { ...this.modelProp };
        newModelProp[this.activeLang].body = value;
        this.$emit('modelEvent', newModelProp);
      }
    },
    image: {
      get() {
        return this.modelProp.image;
      },
      set(value) {
        this.$emit('modelEvent', { ...this.modelProp, image: value });
      }
    },
    targetLanguageCodes() {
      return Object.keys(this.modelProp).filter(k => this.isLanguage(k) && k !== 'default');
    },
    sortedLanguages() {
      const list = [...this.languages];
      list.sort((a, b) => {
        if (a.code === 'default') return -1;
        if (b.code === 'default') return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      return list;
    },
    matchesSearch() {
      const q = (this.languageSearch || '').trim().toLowerCase();
      return (l) => {
        if (!q) return true;
        return (l.name || '').toLowerCase().includes(q) ||
               (l.code || '').toLowerCase().includes(q);
      };
    },
    filteredSelectedLanguages() {
      return this.sortedLanguages.filter(l =>
        this.isLanguageSelected(l.code) && this.matchesSearch(l)
      );
    },
    filteredAvailableLanguages() {
      return this.sortedLanguages.filter(l =>
        !this.isLanguageSelected(l.code) && this.matchesSearch(l)
      );
    },
    selectedLanguageCount() {
      return Object.keys(this.modelProp).filter(k => this.isLanguage(k)).length;
    },
    hasSourceContent() {
      const src = this.modelProp.default || {};
      return TRANSLATABLE_FIELDS.some(k => typeof src[k] === 'string' && src[k].trim() !== '');
    },
    canTranslate() {
      return !this.isTranslating && !this.isShortening && this.hasSourceContent && this.targetLanguageCodes.length > 0;
    },
    hasActiveLangContent() {
      if (this.activeLang === 'default') return false;
      const target = this.modelProp[this.activeLang] || {};
      return TRANSLATABLE_FIELDS.some(k => typeof target[k] === 'string' && target[k].trim() !== '');
    },
    canShorten() {
      return !this.isTranslating && !this.isShortening && this.activeLang !== 'default' && this.hasActiveLangContent;
    }
  },
  data() {
    return {
      activeLang: "default",
      isTranslating: false,
      isShortening: false,
      translationError: null,
      shortenError: null,
      languageModalOpen: false,
      languageSearch: "",
    }
  },
  methods: {
    isLanguage (item){
      return this.languages.find(elem => elem.code === item) ? true : false
    },
    isLanguageSelected(code) {
      return Object.keys(this.modelProp).includes(code);
    },
    toggleLanguageModal(open) {
      this.languageModalOpen = open;
      if (open) {
        this.languageSearch = '';
        this.$nextTick(() => {
          if (this.$refs.languageSearchInput) this.$refs.languageSearchInput.focus();
        });
      }
    },
    toggleLanguage(key) {
      if (key === 'default') return;
      if(Object.keys(this.modelProp).includes(key)) {
        const newModelProp = { ...this.modelProp }
        delete newModelProp[key]
        if(this.activeLang === key) {
          this.activeLang = 'default'
        }
        this.$emit('modelEvent', newModelProp)
      } else {
        const newModelProp = { ...this.modelProp }
        newModelProp[key] = {
          title: "Title",
          subtitle: "Subtitle",
          body: "Message"
        }
        this.$emit('modelEvent', newModelProp)
      }
    },
    toggleActiveLanguage(key) {
      this.activeLang = key
    },
    async getImage(image){
      const appId = this.$store.getters.activeApp;
      let url = await importMedia(this.$refs[image].files[0], appId )
      this.$emit('modelEvent', { ...this.modelProp, image: url });
    },
    languageNameFor(code) {
      const entry = this.languages.find(l => l.code === code);
      return entry ? entry.name : code;
    },
    async translateWithAI() {
      if (!this.canTranslate) return;
      this.translationError = null;

      const src = this.modelProp.default || {};
      const baseTranslations = {};
      for (const key of TRANSLATABLE_FIELDS) {
        const value = src[key];
        if (typeof value === 'string' && value.trim() !== '') {
          baseTranslations[key] = value;
        }
      }
      const targetLanguages = [...this.targetLanguageCodes];

      this.isTranslating = true;
      try {
        const { data } = await managerService.translateMessage(baseTranslations, targetLanguages);
        const translations = (data && data.translations) || {};
        const errors = (data && data.errors) || [];
        const errored = new Set(errors.map(e => e.language));

        const newModelProp = { ...this.modelProp };
        for (const lang of targetLanguages) {
          if (errored.has(lang) || !newModelProp[lang]) continue;
          const merged = { ...newModelProp[lang] };
          const translation = translations[lang];
          for (const key of TRANSLATABLE_FIELDS) {
            if (baseTranslations[key]) {
              if (translation && typeof translation[key] === 'string') merged[key] = translation[key];
            } else {
              merged[key] = "";
            }
          }
          newModelProp[lang] = merged;
        }
        this.$emit('modelEvent', newModelProp);

        if (errors.length > 0) {
          const failed = errors.map(e => this.languageNameFor(e.language)).join(', ');
          const succeeded = Object.keys(translations).length;
          const total = targetLanguages.length;
          this.translationError = `Translated ${succeeded} of ${total} languages. Failed: ${failed}`;
        }
      } catch (err) {
        console.error('Translate with AI failed:', err);
        this.translationError = 'Translation failed. Please try again.';
      } finally {
        this.isTranslating = false;
      }
    },
    buildPushShortenPrompt(lang) {
      const langName = this.languageNameFor(lang) || lang;
      return `Push notifications are shown in users' notification trays where vertical space is limited (typically two short lines on mobile lock screens).\nMake this ${langName} translation noticeably shorter while preserving meaning, tone, and any call-to-action.\nPrefer tighter wording over dropping ideas; keep numbers, brand names, and product names intact.`;
    },
    async shortenWithAI() {
      if (!this.canShorten) return;
      this.shortenError = null;

      const lang = this.activeLang;
      const src = this.modelProp.default || {};
      const target = this.modelProp[lang] || {};

      const baseTranslations = {};
      const translatedContent = {};
      for (const key of TRANSLATABLE_FIELDS) {
        if (typeof src[key] === 'string' && src[key].trim() !== '') baseTranslations[key] = src[key];
        if (typeof target[key] === 'string' && target[key].trim() !== '') translatedContent[key] = target[key];
      }
      if (Object.keys(baseTranslations).length === 0) {
        this.shortenError = 'Source language must have content to shorten against.';
        return;
      }
      if (Object.keys(translatedContent).length === 0) return;

      const customPrompt = this.buildPushShortenPrompt(lang);

      this.isShortening = true;
      try {
        const { data } = await managerService.shortenMessage([
          { language: lang, baseTranslations, translatedContent, customPrompt }
        ]);
        const shortened = data && data.results && data.results[lang];
        const errors = (data && data.errors) || [];
        if (shortened) {
          const newModelProp = { ...this.modelProp };
          newModelProp[lang] = { ...newModelProp[lang], ...shortened };
          this.$emit('modelEvent', newModelProp);
        } else if (errors.length) {
          this.shortenError = `Shorten failed: ${errors[0].message || 'Localizer error'}`;
        } else {
          this.shortenError = 'Shorten failed. Please try again.';
        }
      } catch (err) {
        console.error('Shorten with AI failed:', err);
        this.shortenError = 'Shorten failed. Please try again.';
      } finally {
        this.isShortening = false;
      }
    }
  }
}
</script>

<style scoped>
.phone-frame-container {
  display: flex;
  justify-content: center;
}
.phone-frame-wrapper {
  position: relative;
}
.notification-container {
  position: absolute;
  width: 68%;
  left: 6%;
  top: 30%;
  padding: 5px 5px;
  background: rgba(37, 37, 37, 0.6);
  border-radius: 13px;
}
.notification-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  color: rgba(255,255,255,0.32);
  font-size: 12px;
  padding: 10px 0;
}

.notification-body .title, .notification-body .subtitle, .notification-body .content {
  font-family: Roboto, sans-serif;
  font-weight: 900;
  font-size: 14px;
  letter-spacing: -0.24px;

  color: #FFFFFF;
  margin: 2px 0;
}
.notification-body .content {
  font-weight: normal;
}

.languages-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.language-tabs {
  display: flex;
  align-items: center;
}
.language-tab {
  cursor: pointer;
  padding: 5px 10px;
  margin: 0 5px;
  background: rgba(187, 134, 252, 0.2);
  border: 2px solid #BB86FC;
  border-radius: 4px;
  font-weight: 400;
  color: white;
}

.language-tab.active {
  background-color: #BB86FC;
}

.add-language-button {
  background: #BB86FC;
  padding: 7.5px 10px;
  border-radius: 4px;
  display: flex;
  font-weight: 600;
  align-items: center;
  cursor: pointer;
}
.add-language-button p {
  margin: 0;
}

.translate-ai-button {
  margin-left: 10px;
  background: rgba(187, 134, 252, 0.2);
  border: 2px solid #BB86FC;
  padding: 5.5px 10px;
  border-radius: 4px;
  display: flex;
  font-weight: 600;
  align-items: center;
  cursor: pointer;
  color: white;
}
.translate-ai-button p {
  margin: 0;
}
.translate-ai-button i {
  margin-right: 6px;
}
.translate-ai-button.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.translation-error {
  margin-top: 10px;
  padding: 8px 12px;
  background: rgba(255, 99, 99, 0.15);
  border: 1px solid #ff6363;
  border-radius: 4px;
  color: #ffb4b4;
  font-size: 13px;
}

.spinner {
  display: flex;
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  z-index: 9999;
}

#language-modal {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
  background-color: rgba(10, 10, 10, 0.7);
  z-index: 10;
  backdrop-filter: blur(2px);
}

.language-modal-container {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #1F1F33;
  width: 420px;
  max-width: 90vw;
  height: 560px;
  max-height: 85vh;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.language-modal-container .header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-weight: 600;
  font-size: 16px;
  color: white;
}

.language-modal-container .close-btn {
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.15s;
}

.language-modal-container .close-btn:hover {
  color: white;
}

.search-row {
  display: flex;
  align-items: center;
  padding: 12px 20px 8px 20px;
  position: relative;
}

.search-row .search-icon {
  position: absolute;
  left: 30px;
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
  font-size: 13px;
}

.search-row .clear-icon {
  position: absolute;
  right: 30px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  font-size: 13px;
}

.search-row .clear-icon:hover {
  color: white;
}

.language-search-input {
  width: 100%;
  padding: 10px 32px 10px 32px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, background-color 0.15s;
}

.language-search-input:focus {
  border-color: #BB86FC;
  background-color: rgba(255, 255, 255, 0.08);
}

.language-search-input::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.language-list {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  padding: 4px 8px 8px 8px;
}

.language-section-header {
  padding: 10px 12px 6px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 600;
  position: sticky;
  top: 0;
  background-color: #1F1F33;
  z-index: 1;
}

.language-list::-webkit-scrollbar {
  width: 8px;
}
.language-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}
.language-list::-webkit-scrollbar-track {
  background: transparent;
}

.language-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin: 2px 0;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: background-color 0.12s;
  font-size: 14px;
}

.language-row:hover {
  background-color: rgba(187, 134, 252, 0.12);
}

.language-row.selected {
  background-color: rgba(187, 134, 252, 0.22);
}

.language-row.selected:hover {
  background-color: rgba(187, 134, 252, 0.3);
}

.language-row.locked {
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.55);
}

.language-row.locked:hover {
  background-color: transparent;
}

.language-row-trailing {
  display: flex;
  align-items: center;
  color: #BB86FC;
  font-size: 13px;
  min-width: 16px;
  justify-content: flex-end;
}

.language-row .remove-icon {
  color: rgba(255, 255, 255, 0.35);
  transition: color 0.12s;
}

.language-row.selected:hover .remove-icon {
  color: #ff6b6b;
}

.language-row .add-icon {
  color: rgba(255, 255, 255, 0.3);
  transition: color 0.12s;
}

.language-row:hover .add-icon {
  color: #BB86FC;
}

.language-row.locked .language-row-trailing {
  color: rgba(255, 255, 255, 0.3);
}

.language-empty {
  padding: 24px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
}

.modal-footer {
  padding: 12px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: flex-end;
}

.modal-done-btn {
  background-color: #BB86FC;
  color: #1F1F33;
  border: none;
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.15s;
}

.modal-done-btn:hover {
  background-color: #CFA3FF;
}

.inline{
  display: block;
  overflow-x: scroll;
  white-space: nowrap;
}


</style>